/**
 * Executes confirmed agent actions against the local ERP API (localhost:5001).
 */

import i18n from '../../i18n';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  AGENT_ACTION_TYPES,
  AGENT_LOCAL_API_PATHS,
} from '../constants/actionTypes';
import {
  getAgentDocumentTypeLabel,
  prepareAgentDocumentHandoff,
} from './agentDocumentHandoff';

const lp = (key, opts) => i18n.t(`modules.shared.aiAgent.localExecutor.${key}`, opts);

async function executeCreateDocumentHandoff(params) {
  const handoff = await prepareAgentDocumentHandoff(params);

  if (!handoff.success) {
    return { success: false, error: handoff.error || lp('document_prepare_failed') };
  }

  const partnerLabel =
    params.client ||
    params.clientName ||
    params.supplier ||
    params.supplierName ||
    '---';
  const docLabel = getAgentDocumentTypeLabel(handoff.docType);

  return {
    success: true,
    action: AGENT_ACTION_TYPES.CREATE_INVOICE,
    handoff: true,
    docType: handoff.docType,
    openPath: handoff.openPath,
    conversionData: handoff.conversionData,
    message: lp('draft_prepared', { docLabel, partner: partnerLabel }),
  };
}

async function executeCreateClient(params) {
  const companyName = params.name || params.client || params.company_name;
  if (!companyName) {
    return { success: false, error: lp('client_name_required') };
  }

  const result = await localApiAdapter.post(AGENT_LOCAL_API_PATHS.create_client, {
    company_name: companyName,
    client_type: params.type || params.client_type || lp('default_client_type'),
    status: params.status || 'active',
    email: params.email || null,
    phone1: params.phone || params.phone1 || null,
    address: params.address || null,
    notes: params.notes || lp('created_via_agent'),
  });

  return {
    success: true,
    action: AGENT_ACTION_TYPES.CREATE_CLIENT,
    data: result?.data || result,
    message: lp('client_created', { name: companyName }),
  };
}

async function executeCreateProduct(params) {
  const name = params.name || params.product;
  if (!name) {
    return { success: false, error: lp('product_name_required') };
  }

  const result = await localApiAdapter.post(AGENT_LOCAL_API_PATHS.create_product, {
    name,
    price: params.price || 0,
    stock: params.stock || 0,
    category: params.category || null,
    description: params.description || null,
    notes: params.notes || lp('created_via_agent'),
  });

  return {
    success: true,
    action: AGENT_ACTION_TYPES.CREATE_PRODUCT,
    data: result?.data || result,
    message: lp('product_created', { name }),
  };
}

const ACTION_HANDLERS = {
  [AGENT_ACTION_TYPES.CREATE_INVOICE]: executeCreateDocumentHandoff,
  [AGENT_ACTION_TYPES.CREATE_CLIENT]: executeCreateClient,
  [AGENT_ACTION_TYPES.CREATE_PRODUCT]: executeCreateProduct,
};

/**
 * @param {{ type?: string, params?: object, action?: string, entity?: string }} command
 */
export async function executeAgentAction(command) {
  if (!command) {
    return { success: false, error: lp('no_command') };
  }

  const actionType =
    command.type ||
    (command.action === 'create' && command.entity === 'client'
      ? AGENT_ACTION_TYPES.CREATE_CLIENT
      : command.action === 'create' && command.entity === 'product'
        ? AGENT_ACTION_TYPES.CREATE_PRODUCT
        : command.action === 'create' && command.entity === 'invoice'
          ? AGENT_ACTION_TYPES.CREATE_INVOICE
          : null);

  const params = command.params || command;

  const handler = actionType ? ACTION_HANDLERS[actionType] : null;

  if (!handler) {
    return {
      success: false,
      error: lp('unsupported_action', { action: actionType || command.action || 'unknown' }),
    };
  }

  try {
    return await handler(params);
  } catch (error) {
    console.error('AI Agent local execution error:', error);
    return {
      success: false,
      error: error.message || lp('execution_error'),
    };
  }
}
